import type { WorkOrderTourSheetRequest } from "../../../../../../../../../api/kehrblatt/work-orders"
import {
  formatDateParam,
  normalizeDateRangeFilter,
  type WorkOrderDateRangeFilter,
} from "../work-order-filters"
import { normalizeStringList } from "../query"

export type TourSheetPrintOptions = {
  dateRange: WorkOrderDateRangeFilter
  employeeIds: string[]
}

export const createTourSheetPrintDateRange = (now = new Date()): WorkOrderDateRangeFilter => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value)
  // Date inputs carry local calendar dates; Berlin determines today's day, not its timestamp.
  const start = new Date(part("year"), part("month") - 1, part("day"))

  return { start, end: new Date(start.getTime()) }
}

export const tourSheetPrintDateRangeError = (
  dateRange: WorkOrderDateRangeFilter,
): string | null => {
  const normalized = normalizeDateRangeFilter(dateRange)
  const start = formatDateParam(normalized.start)
  const end = formatDateParam(normalized.end)

  if (!start || !end) return "Bitte wählen Sie einen vollständigen Terminzeitraum."

  if (end < start) return "Das Ende des Terminzeitraums darf nicht vor dem Beginn liegen."

  return null
}

export const buildWorkOrderTourSheetRequest = ({
  organizationId,
  dateRange,
  employeeIds,
}: {
  organizationId?: string | null
} & TourSheetPrintOptions): WorkOrderTourSheetRequest | null => {
  if (!organizationId || tourSheetPrintDateRangeError(dateRange)) return null

  const normalizedEmployeeIds = Array.from(new Set(normalizeStringList(employeeIds)))

  return {
    organization_id: organizationId,
    archive_scope: "ACTIVE",
    appointment_date_from: formatDateParam(dateRange.start),
    appointment_date_to: formatDateParam(dateRange.end),
    employee_ids: normalizedEmployeeIds.length ? normalizedEmployeeIds : undefined,
  }
}

export const openTourSheetDownload = ({
  blob,
  filename,
}: {
  blob: Blob
  filename: string
}): string | null => {
  if (typeof URL === "undefined" || typeof window === "undefined") return null

  const objectUrl = URL.createObjectURL(blob)
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer")

  if (opened) return objectUrl

  const anchor = document.createElement("a")
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  return objectUrl
}
